import React, { useState } from 'react';
import { getConsent, setConsent, type Consent } from '../../utils/cookieConsent';
import { initGA, trackPageView } from '../../utils/analytics';
import './CookieConsent.css';

const CookieIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
    <path
      d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <circle cx="9" cy="11" r="1.15" fill="currentColor" />
    <circle cx="13.5" cy="15" r="1.15" fill="currentColor" />
    <circle cx="15.5" cy="10" r="1" fill="currentColor" />
    <circle cx="8.5" cy="15.5" r="1" fill="currentColor" />
  </svg>
);

const CookieConsent: React.FC = () => {
  const [consent, setConsentState] = useState<Consent | null>(() => getConsent());
  // Open the banner on first load (no prior choice). After a choice the banner
  // is closed and only the floating cookie button remains.
  const [open, setOpen] = useState<boolean>(() => getConsent() === null);

  const choose = (value: Consent) => {
    setConsent(value);
    setConsentState(value);
    setOpen(false);
    if (value === 'granted') {
      initGA();
      trackPageView(window.location.pathname + window.location.search);
    }
  };

  if (open) {
    return (
      <div className="cc-banner" role="dialog" aria-label="Cookie consent" aria-live="polite">
        {consent !== null && (
          <button
            type="button"
            className="cc-close"
            aria-label="Close cookie settings"
            onClick={() => setOpen(false)}
          >
            ×
          </button>
        )}
        <p className="cc-text">
          We use cookies to analyze traffic and improve your experience. You can accept or
          decline analytics cookies. See our{' '}
          <a href="https://offerflow.pro/cookie-policy" target="_blank" rel="noopener noreferrer">
            Cookie Policy
          </a>
          .
        </p>
        <div className="cc-actions">
          <button type="button" className="cc-btn cc-btn-decline" onClick={() => choose('denied')}>
            Decline
          </button>
          <button type="button" className="cc-btn cc-btn-accept" onClick={() => choose('granted')}>
            Accept
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      className="cc-fab"
      aria-label="Cookie settings"
      title="Cookie settings"
      onClick={() => setOpen(true)}
    >
      <CookieIcon />
    </button>
  );
};

export default CookieConsent;
